import { useState, useEffect, useMemo, useRef } from 'react'
import { useAuth } from '../../contexts/AuthContext'
import * as api from '../../lib/api'
import DateRangeFilter from '../../components/DateRangeFilter'
import Modal from '../../components/Modal'
import { 
  ResponsiveContainer, 
  BarChart, 
  Bar, 
  XAxis, 
  YAxis, 
  Tooltip as RechartsTooltip, 
  Legend 
} from 'recharts'

import { DashboardSkeleton } from '../../components/Skeleton/Skeleton'
import CustomSelect from '../../components/CustomSelect/CustomSelect'
import './DashboardPage.css'

import { formatMoney } from './utils/formatMoney'
import { todayIso, yesterdayIso, monthStartIso, monthEndIso, quarterStartIso, quarterEndIso } from './utils/dateHelpers'
import { calculateDailyFinancials } from './utils/calculateDailyFinancials'
import { FinanceStatsCards } from './components/FinanceStatsCards'
import { OpsStatsCards } from './components/OpsStatsCards'
import { DayDetailsModal } from './components/DayDetailsModal'
import OnboardingChecklist from './components/OnboardingChecklist'

export default function DashboardPage({ onPageChange }) {
  const { profile, activePvz, userPvzs } = useAuth()
  const pvzGenRef = useRef(0)

  // По умолчанию: с 1-го числа текущего месяца по последний день месяца
  const [filterStartDate, setFilterStartDate] = useState(monthStartIso())
  const [filterEndDate, setFilterEndDate] = useState(monthEndIso())
  const [filterEmployee, setFilterEmployee] = useState('')
  const [employees, setEmployees] = useState([])
  
  const [stats, setStats] = useState({
    revenue: 0,
    accepted: 0,
    issued: 0,
    returns: 0,
    custom: 0,
    deductionsSum: 0
  })
  
  const [rawLogs, setRawLogs] = useState({
    acceptedLogs: [],
    issuedLogs: [],
    issuedLogsPast30: [],
    returnLogs: [],
    incomes: [],
    expenses: [],
    salaries: [],
    plans: [],
    reserves: [],
    partnerTurnovers: []
  })

  const [averageDailyPayrollPast30, setAverageDailyPayrollPast30] = useState(0)
  const [averageDailyVarExpensesPast30, setAverageDailyVarExpensesPast30] = useState(0)
  const [averageDailyPartnerCommissionPast30, setAverageDailyPartnerCommissionPast30] = useState(0)


  const [useEstimatedRevenue, setUseEstimatedRevenue] = useState(false)
  const [salaryConfig, setSalaryConfig] = useState(null)
  const [missingTurnoverDays, setMissingTurnoverDays] = useState([])
  const [turnoverInputs, setTurnoverInputs] = useState({})
  const [upcomingPayouts, setUpcomingPayouts] = useState([])
  const [salaryPayoutCandidates, setSalaryPayoutCandidates] = useState([])
  const [salaryCandidatesStatus, setSalaryCandidatesStatus] = useState('idle') // 'idle' | 'loading' | 'success' | 'error'
  const [salaryCandidatesError, setSalaryCandidatesError] = useState(null)
  const [loadingPayouts, setLoadingPayouts] = useState(false)
  const [paymentModalPayout, setPaymentModalPayout] = useState(null)
  const [paymentSubmitting, setPaymentSubmitting] = useState(false)
  const [paymentError, setPaymentError] = useState(null)
  const [payoutsRefreshKey, setPayoutsRefreshKey] = useState(0)
  const [loading, setLoading] = useState(true)
  const [noOpsWarning, setNoOpsWarning] = useState(null) 
  
  const isAdmin = profile?.role === 'admin' || profile?.role === 'owner' || profile?.role === 'admin_employee'
  
  const [forecast, setForecast] = useState(null)
  const [loadingForecast, setLoadingForecast] = useState(false)
  const [forecastError, setForecastError] = useState(null)

  const loadForecast = async (force = false) => {
    if (!isAdmin || !activePvz?.id) return
    setLoadingForecast(true)
    setForecastError(null)
    try {
      const data = await api.getWorkloadForecast(activePvz.id, force)
      if (data?.unavailable) {
        setForecast(null)
        setForecastError(data.message || 'Сервис AI-прогноза временно недоступен')
      } else {
        setForecast(data)
      }
    } catch (err) {
      console.error('Failed to load forecast', err)
      setForecastError(err.message || 'Не удалось загрузить прогноз')
    } finally {
      setLoadingForecast(false)
    }
  }

  // Немедленный сброс метрик Дашборда при смене ПВЗ или аккаунта
  useEffect(() => {
    pvzGenRef.current += 1
    setSalaryConfig(null)
    setSalaryPayoutCandidates([])
    setUpcomingPayouts([])
    setPaymentModalPayout(null)
    setPaymentError(null)
    setLoadingPayouts(true)
    setSalaryCandidatesStatus('loading')
    setSalaryCandidatesError(null)
    setStats({
      revenue: 0,
      accepted: 0,
      issued: 0,
      returns: 0,
      custom: 0,
      deductionsSum: 0
    })
    setRawLogs({
      acceptedLogs: [],
      issuedLogs: [],
      issuedLogsPast30: [],
      returnLogs: [],
      incomes: [],
      expenses: [],
      salaries: [],
      plans: [],
      reserves: [],
      partnerTurnovers: []
    })
    setForecast(null)
    setLoading(true)
  }, [activePvz?.id, profile?.id])

  // Состояния для интерактивного графика
  const [activeChartTab, setActiveChartTab] = useState('ops') // 'ops' | 'finance'
  const [chartOpsSettings, setChartOpsSettings] = useState({ 
    issued: true, 
    accepted: true, 
    returns: true,
    salary: true 
  })
  const [chartFinSettings, setChartFinSettings] = useState({ income: true, expense: true, profit: true })
  const [selectedDayDetails, setSelectedDayDetails] = useState(null)

  const loadMissingTurnover = async () => {
    if (salaryConfig?.turnoverPercent > 0) {
      try {
        const days = await api.getMissingTurnoverDays(7)
        setMissingTurnoverDays(days)
      } catch (err) {
        console.error('Failed to load missing turnover days', err)
      }
    } else {
      setMissingTurnoverDays([])
    }
  }

  useEffect(() => {
    if (!activePvz?.id) {
      setEmployees([])
      setSalaryConfig(null)
      return
    }

    const currentGen = pvzGenRef.current

    api.getAllEmployeesList(activePvz.id)
      .then(data => {
        if (currentGen !== pvzGenRef.current) return
        setEmployees(Array.isArray(data) ? data : [])
      })
      .catch(err => {
        if (currentGen !== pvzGenRef.current) return
        console.error('[DashboardPage] Failed to load employees:', err)
        setEmployees([])
      })

    api.getSalaryConfig(activePvz.id)
      .then(cfg => {
        if (currentGen !== pvzGenRef.current) return
        setSalaryConfig(cfg || null)
      })
      .catch(err => {
        if (currentGen !== pvzGenRef.current) return
        console.error('[DashboardPage] Failed to load salary config:', err)
        setSalaryConfig(null)
      })
  }, [activePvz?.id])

  // Загрузка кандидатов на выплату зарплаты (активные + архивные с исторической активностью на ПВЗ для админа; self для сотрудника)
  useEffect(() => {
    if (!activePvz?.id) {
      setSalaryPayoutCandidates([])
      setSalaryCandidatesStatus('idle')
      setSalaryCandidatesError(null)
      return
    }

    const currentGen = pvzGenRef.current
    setSalaryCandidatesStatus('loading')
    setSalaryCandidatesError(null)

    if (isAdmin) {
      api.getSalaryCandidates(activePvz.id, profile?.org_id)
        .then(data => {
          if (currentGen !== pvzGenRef.current) return
          setSalaryPayoutCandidates(Array.isArray(data) ? data : [])
          setSalaryCandidatesStatus('success')
          setSalaryCandidatesError(null)
        })
        .catch(err => {
          if (currentGen !== pvzGenRef.current) return
          console.error('[DashboardPage] Failed to load salary candidates:', err)
          setSalaryPayoutCandidates([])
          setSalaryCandidatesStatus('error')
          setSalaryCandidatesError(err?.message || 'Не удалось загрузить данные о выплатах')
        })
    } else if (profile?.id) {
      setSalaryPayoutCandidates([{
        ...profile,
        name: profile.full_name || profile.name
      }])
      setSalaryCandidatesStatus('success')
      setSalaryCandidatesError(null)
    } else {
      setSalaryPayoutCandidates([])
      setSalaryCandidatesStatus('idle')
      setSalaryCandidatesError(null)
    }
  }, [activePvz?.id, isAdmin, profile?.id, profile?.org_id, payoutsRefreshKey])

  useEffect(() => {
    loadMissingTurnover()
  }, [salaryConfig])

  const handleSaveTurnover = async (date) => {
    const amount = turnoverInputs[date]
    if (!amount || isNaN(amount) || amount <= 0) return
    try {
      await api.saveDailyTurnover(date, amount, profile?.id)
      setTurnoverInputs(prev => {
        const next = { ...prev }
        delete next[date]
        return next
      })
      loadMissingTurnover()
    } catch (err) {
      console.error('Failed to save turnover', err)
    }
  }

  // Расчет напоминаний выплат (P0-1 & P0-2: batch in-memory, read-only, без N+1)
  useEffect(() => {
    if (!activePvz?.id || !salaryPayoutCandidates || salaryPayoutCandidates.length === 0 || !salaryConfig) {
      setUpcomingPayouts([])
      setLoadingPayouts(false)
      return
    }

    const currentGen = pvzGenRef.current
    const calculatePayouts = async () => {
      setLoadingPayouts(true)
      try {
        const results = await api.calculateUpcomingPayoutsBatch(
          activePvz.id,
          salaryPayoutCandidates,
          salaryConfig,
          {
            isAdmin,
            currentUserId: profile?.id,
            today: new Date(),
            candidateScope: 'salary'
          }
        )
        if (currentGen !== pvzGenRef.current) return
        setUpcomingPayouts(results)
      } catch (e) {
        if (currentGen !== pvzGenRef.current) return
        console.error('Ошибка расчета напоминаний выплат:', e)
        setUpcomingPayouts([])
      } finally {
        if (currentGen === pvzGenRef.current) {
          setLoadingPayouts(false)
        }
      }
    }

    calculatePayouts()
  }, [activePvz?.id, salaryPayoutCandidates, salaryConfig, isAdmin, profile?.id, payoutsRefreshKey])

  // Загрузка статистики и наполнение сырых логов для графиков
  useEffect(() => {
    if (!profile?.id || !activePvz?.id) return
    
    const loadStats = async () => {
      setLoading(true)
      try {
        const targetEmployeeId = isAdmin ? filterEmployee : profile.id
        const filters = { startDate: filterStartDate, endDate: filterEndDate }
        if (targetEmployeeId) filters.employeeId = targetEmployeeId

        const startParts = filterStartDate.split('-').map(Number);
        const startDateObj = new Date(startParts[0], startParts[1] - 1, startParts[2]);
        startDateObj.setDate(startDateObj.getDate() - 30);
        const pStartMinus30 = `${startDateObj.getFullYear()}-${String(startDateObj.getMonth() + 1).padStart(2, '0')}-${String(startDateObj.getDate()).padStart(2, '0')}`;

        // P0-3: Единый broad запрос work_logs за [pStartMinus30, filterEndDate] для устранения дублирования
        const broadLogs = await api.getWorkLogs('all', { startDate: pStartMinus30, endDate: filterEndDate, pvzId: activePvz.id })
        const allLogs = broadLogs.filter(log => {
          const matchDate = log.work_date >= filterStartDate && log.work_date <= filterEndDate
          const matchEmp = !targetEmployeeId || log.employee_id === targetEmployeeId
          return matchDate && matchEmp
        })

        const acceptedLogs = allLogs.filter(log => log.type === 'acceptance')
        const issuedLogs = allLogs.filter(log => log.type === 'issued')
        const returnLogs = allLogs.filter(log => log.type === 'returns')

        const accepted = acceptedLogs.reduce((acc, log) => acc + (log.count || 0) + (log.count_late || 0), 0)
        const acceptedOnTime = acceptedLogs.reduce((acc, log) => acc + (log.count || 0), 0)
        const acceptedLate = acceptedLogs.reduce((acc, log) => acc + (log.count_late || 0), 0)
        const issued = issuedLogs.reduce((acc, log) => acc + (log.count || 0), 0)
        const returnsBoxes = returnLogs.length
        const returnsOnTime = returnLogs.reduce((acc, log) => acc + (log.count || 0), 0)
        const returnsLate = returnLogs.reduce((acc, log) => acc + (log.count_late || 0), 0)
        const custom = allLogs.filter(l => l.type && l.type.startsWith('custom:')).reduce((acc, log) => acc + (log.count || 0), 0)

        // Логи выдачи за 30 дней до начала для расчета скользящего оборота (in-memory фильтрация broadLogs)
        const issuedLogsPast30 = broadLogs.filter(log => log.type === 'issued')

        let deductionsSum = 0
        try {
          const { getMyDeductions, getDeductions } = await import('../../lib/api')
          let deductionsData = []
          if (isAdmin) {
            const allDeds = await getDeductions(filterStartDate, filterEndDate, activePvz.id)
            deductionsData = targetEmployeeId ? allDeds.filter(d => d.employee_id === targetEmployeeId) : allDeds
          } else {
            deductionsData = await getMyDeductions(filterStartDate, filterEndDate)
          }
          deductionsSum = deductionsData.reduce((sum, d) => sum + parseFloat(d.amount), 0)
        } catch (e) {
          console.error('Ошибка загрузки удержаний', e)
        }

        let financeStats = null
        let incomes = []
        let expenses = []
        let salaries = []
        let plans = []
        let reserves = []
        let partnerTurnovers = []

        if (isAdmin) {
          try {
            // P0-3: Устранение дублирующего запроса сотрудников, если они уже есть в state
            const employeesPromise = (employees && employees.length > 0)
              ? Promise.resolve(employees)
              : api.getAllEmployeesList(activePvz.id)

            // Запрашиваем с расширением pStartMinus30 для доходов, расходов и партнеров
            const [inc, exp, pay, emps, activePlans, res, turnovers] = await Promise.all([
              api.getWbIncomes(activePvz.id, pStartMinus30, filterEndDate),
              api.getExpenses(activePvz.id, pStartMinus30, filterEndDate),
              api.getPayments({ periodStart: filterStartDate, periodEnd: filterEndDate, pvzId: activePvz.id }),
              employeesPromise,
              api.getFixedExpenseTemplates(activePvz.id),
              api.getReserves(activePvz.id, filterStartDate, filterEndDate),
              api.getPartnerTurnovers(activePvz.id, pStartMinus30, filterEndDate)
            ])

            incomes = inc
            expenses = exp
            plans = activePlans
            reserves = res || []
            partnerTurnovers = turnovers || []

            // Вычисляем настройки прогноза
            const forecastAutoCalc = salaryConfig?.wbRevenueAutoCalc !== undefined ? salaryConfig.wbRevenueAutoCalc : true
            const manualVal = salaryConfig?.wbRevenuePerIssued || 3.0
            const forecastManualRate = manualVal > 10 ? 3.0 : manualVal

            // Считаем dailyFinancials
            const calculatedDailyFin = calculateDailyFinancials(inc, issuedLogsPast30, filterStartDate, filterEndDate, forecastAutoCalc, forecastManualRate)

            // Фактические и прогнозируемые доходы Wildberries
            const actualWbIncome = Object.values(calculatedDailyFin.dailyData)
              .filter(day => day.type === 'fact')
              .reduce((sum, day) => sum + day.income, 0)

            const estimatedWbIncome = Object.values(calculatedDailyFin.dailyData).reduce((sum, day) => sum + day.income, 0)

            const totalWbIncome = useEstimatedRevenue ? estimatedWbIncome : actualWbIncome

            const otherSourceIncome = inc.filter(item => item.date >= filterStartDate && item.date <= filterEndDate && item.source && item.source !== 'Wildberries')
            const otherSourceIncomeSum = otherSourceIncome.reduce((sum, item) => sum + Number(item.amount), 0)

            const totalIncome = totalWbIncome + otherSourceIncomeSum

            // Расчет ФОТ (baseSalaryAccrued) за текущий период
            const activeEmps = emps.filter(emp => !emp.is_legacy)
            const [rawSalaries, rawPastSalaries] = await Promise.all([
              api.calculateTeamSalaries(activePvz.id, filterStartDate, filterEndDate, activeEmps, [], { mode: 'analytics' }),
              api.calculateTeamSalaries(activePvz.id, pStartMinus30, filterStartDate, activeEmps, [], { mode: 'analytics' })
            ])
            salaries = rawSalaries
            const totalPayroll = rawSalaries.reduce((sum, s) => sum + Math.max(s.total || 0, 0), 0)

            // Расчет средних показателей ФОТ, Переменных и Партнеров за прошлые 30 дней для прогноза
            const pastPayrollTotal = rawPastSalaries.reduce((sum, s) => sum + Math.max(s.total || 0, 0), 0)
            const avgDailyPayroll = pastPayrollTotal / 30
            setAverageDailyPayrollPast30(avgDailyPayroll)

            const pastExpenses = exp.filter(e => e.date >= pStartMinus30 && e.date < filterStartDate && e.type !== 'fixed_payment' && e.is_paid !== false)
            const pastVarExpensesTotal = pastExpenses.reduce((sum, e) => sum + Number(e.amount), 0)
            const avgDailyVarExpenses = pastVarExpensesTotal / 30
            setAverageDailyVarExpensesPast30(avgDailyVarExpenses)

            const pastPartnerTurnovers = turnovers.filter(t => {
              const d = t.entry_date?.split('T')[0]
              return d >= pStartMinus30 && d < filterStartDate
            })
            const pastPartnerTotal = pastPartnerTurnovers.reduce((sum, t) => sum + Number(t.commission_amount || 0), 0)
            const avgDailyPartnerCommission = pastPartnerTotal / 30
            setAverageDailyPartnerCommissionPast30(avgDailyPartnerCommission)

            // Вычисляем количество прогнозных (будущих) дней в выбранном периоде
            const forecastDaysCount = useEstimatedRevenue && calculatedDailyFin.lastActualDate
              ? calculatedDailyFin.days.filter(d => d > calculatedDailyFin.lastActualDate).length
              : 0

            // Переменные расходы за выбранный период (фильтруем те, что входят в filterStartDate - filterEndDate)
            const selectedPeriodExpenses = exp.filter(e => e.date >= filterStartDate && e.date <= filterEndDate)
            const varExpensesList = selectedPeriodExpenses.filter(e => e.type !== 'fixed_payment' && e.is_paid !== false)
            const actualVariableExpenses = varExpensesList.reduce((sum, e) => sum + Number(e.amount), 0)
            const variableExpenses = actualVariableExpenses + (useEstimatedRevenue ? (forecastDaysCount * avgDailyVarExpenses) : 0)

            // Вычисляем maxIncomeDateStr (дата последнего дохода)
            const maxIncomeDateStr = calculatedDailyFin.lastActualDate

            // Рассчитываем постоянные расходы за отфильтрованный период точно по логике аналитики
            let fixedPaidExpenses = 0
            activePlans.filter(p => p.is_active !== false).forEach(p => {
              const amount = Number(p.amount || p.monthly_amount || 0);
              if (amount <= 0 || isNaN(amount)) return;
              
              if (!maxIncomeDateStr) return; // Нет доходов = 0 постоянных расходов
              
              const rStart = new Date(filterStartDate + 'T00:00:00');
              const rEnd = new Date(filterEndDate + 'T23:59:59');
              
              const cutoffDateStr = useEstimatedRevenue ? filterEndDate : (maxIncomeDateStr || filterStartDate);
              const cutoffEnd = new Date(Math.min(rEnd.getTime(), new Date(cutoffDateStr + 'T23:59:59').getTime()));
              
              if (rStart > cutoffEnd) return;
              
              let proratedTotal = 0;
              let current = new Date(rStart.getFullYear(), rStart.getMonth(), 1);
              
              while (current <= cutoffEnd) {
                const mYear = current.getFullYear();
                const mMonth = current.getMonth();
                const daysInMonth = new Date(mYear, mMonth + 1, 0).getDate();
                
                const mStart = new Date(mYear, mMonth, 1);
                const mEnd = new Date(mYear, mMonth + 1, 0, 23, 59, 59);
                
                const overlapStart = Math.max(mStart.getTime(), rStart.getTime());
                const overlapEnd = Math.min(mEnd.getTime(), cutoffEnd.getTime());
                
                if (overlapStart <= overlapEnd) {
                  const overlapDays = Math.ceil((overlapEnd - overlapStart) / (1000 * 60 * 60 * 24));
                  proratedTotal += (amount / daysInMonth) * overlapDays;
                }
                
                current.setMonth(current.getMonth() + 1);
              }
              fixedPaidExpenses += proratedTotal;
            });

            // Налог УСН 6%
            const calculatedTax = totalIncome * 0.06

            // Расходы на партнёров (selectedPeriodPartnerTurnovers)
            const selectedPeriodPartnerTurnovers = turnovers.filter(t => {
              const d = t.entry_date?.split('T')[0]
              return d >= filterStartDate && d <= filterEndDate
            })
            const actualPartnerCommission = selectedPeriodPartnerTurnovers.reduce((sum, t) => sum + Number(t.commission_amount || 0), 0)
            const totalPartnerCommission = actualPartnerCommission + (useEstimatedRevenue ? (forecastDaysCount * avgDailyPartnerCommission) : 0)

            // Расходы + Налог = Переменные + Постоянные + Налог + Партнёры (БЕЗ ФОТ)
            const totalExpenses = variableExpenses + fixedPaidExpenses + calculatedTax + totalPartnerCommission

            // Чистая прибыль (БЕЗ вычета резервов, чтобы совпадать с Аналитикой)
            // Чистая прибыль = Доходы − ФОТ − Переменные − Постоянные − Налог − Партнёры
            const netProfit = totalIncome - totalPayroll - variableExpenses - fixedPaidExpenses - calculatedTax - totalPartnerCommission

            financeStats = { totalIncome, totalExpenses, totalPayroll, netProfit }
          } catch (e) {
            console.error('Ошибка загрузки финансов для дашборда', e)
          }
        } else {
          // Для обычного сотрудника подгружаем его личную зарплату для вывода на график
          try {
            const singleEmpSal = await api.calculateTeamSalaries(
              activePvz.id,
              filterStartDate,
              filterEndDate,
              [profile],
              [],
              { mode: 'analytics' }
            )
            salaries = [{
              employeeId: profile.id,
              employeeName: profile.name,
              subtotal: singleEmpSal[0]?.subtotal || 0,
              dailyDetails: singleEmpSal[0]?.dailyDetails || {}
            }]
          } catch (e) {
            console.error('Ошибка загрузки личной зарплаты сотрудника', e)
          }
        }

        setRawLogs({
          acceptedLogs,
          issuedLogs,
          issuedLogsPast30,
          returnLogs,
          incomes,
          expenses,
          salaries,
          plans,
          reserves,
          partnerTurnovers
        })

        setStats({ accepted, acceptedOnTime, acceptedLate, issued, returnsBoxes, returnsOnTime, returnsLate, custom, deductionsSum, financeStats })
      } catch (err) {
        console.error('Ошибка загрузки статистики дашборда', err)
      } finally {
        setLoading(false)
      }
    }

    loadStats()
  }, [profile?.id, activePvz?.id, filterStartDate, filterEndDate, filterEmployee, isAdmin, salaryConfig, useEstimatedRevenue, employees.length])

  // Вычисление данных для графиков по дням
  const chartData = useMemo(() => {
    if (!filterStartDate || !filterEndDate) return []

    const dailyMap = {}
    const allDates = []
    const startParts = filterStartDate.split('-').map(Number)
    const endParts = filterEndDate.split('-').map(Number)
    let currentDay = new Date(startParts[0], startParts[1] - 1, startParts[2])
    const endDay = new Date(endParts[0], endParts[1] - 1, endParts[2])

    const fmtDate = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`

    const activePlans = (rawLogs.plans || []).filter(p => p.is_active !== false)

    // Вычисляем dailyFinancials для графика
    const forecastAutoCalc = salaryConfig?.wbRevenueAutoCalc !== undefined ? salaryConfig.wbRevenueAutoCalc : true
    const manualVal = salaryConfig?.wbRevenuePerIssued || 3.0
    const forecastManualRate = manualVal > 10 ? 3.0 : manualVal

    const calcDailyFin = calculateDailyFinancials(
      rawLogs.incomes || [],
      rawLogs.issuedLogsPast30 || [], // используем расширенные логи выдачи
      filterStartDate,
      filterEndDate,
      forecastAutoCalc,
      forecastManualRate
    )

    const maxIncomeDateStr = calcDailyFin.lastActualDate

    // Инициализируем дни
    while (currentDay <= endDay) {
      const dStr = fmtDate(currentDay)
      allDates.push(dStr)

      const dateObj = new Date(dStr + 'T00:00:00')
      const daysInMonth = new Date(dateObj.getFullYear(), dateObj.getMonth() + 1, 0).getDate()
      const daysOfWeek = ['Вс', 'Пн', 'Вт', 'Ср', 'Чт', 'Пт', 'Сб']
      const dayName = daysOfWeek[dateObj.getDay()]
      
      const dayExpenses = []
      let dailyFixed = 0

      const cutoffDateStr = useEstimatedRevenue ? filterEndDate : (maxIncomeDateStr || filterStartDate);
      if (maxIncomeDateStr && dStr <= cutoffDateStr) {
        activePlans.forEach(p => {
          const amt = Number(p.amount || p.monthly_amount || 0)
          const dailyAmt = isNaN(amt) ? 0 : amt / daysInMonth
          if (dailyAmt > 0) {
            dailyFixed += dailyAmt
            dayExpenses.push({ name: `Пост. расход: ${p.category || p.title || 'Регулярный платёж'}`, amount: dailyAmt })
          }
        })
      }

      dailyMap[dStr] = {
        date: dStr,
        displayDate: `${dStr.split('-').slice(1).reverse().join('.')} ${dayName}`,
        // Операции
        accepted: 0,
        acceptedOnTime: 0,
        acceptedLate: 0,
        issued: 0,
        returns: 0,
        returnsOnTime: 0,
        returnsLate: 0,
        salary: 0,
        // Финансы
        income: 0,
        expense: dailyFixed,
        profit: 0,
        realProfit: 0,
        details: { 
          incomes: [], 
          expenses: dayExpenses, 
          ops: [] 
        }
      }
      currentDay.setDate(currentDay.getDate() + 1)
    }

    // Заполняем операциями
    if (rawLogs.acceptedLogs) {
      rawLogs.acceptedLogs.forEach(log => {
        const d = log.work_date
        if (dailyMap[d]) {
          const onTime = Number(log.count || 0)
          const late = Number(log.count_late || 0)
          dailyMap[d].accepted += (onTime + late)
          dailyMap[d].acceptedOnTime += onTime
          dailyMap[d].acceptedLate += late
          if (onTime > 0) dailyMap[d].details.ops.push({ name: 'Принято товаров (вовремя)', value: onTime })
          if (late > 0) dailyMap[d].details.ops.push({ name: 'Принято товаров (с опозданием)', value: late })
        }
      })
    }

    if (rawLogs.issuedLogs) {
      rawLogs.issuedLogs.forEach(log => {
        const d = log.work_date
        if (dailyMap[d]) {
          const count = Number(log.count || 0)
          dailyMap[d].issued += count
          if (count > 0) dailyMap[d].details.ops.push({ name: 'Выдано товаров', value: count })
        }
      })
    }

    if (rawLogs.returnLogs) {
      rawLogs.returnLogs.forEach(log => {
        const d = log.work_date
        if (dailyMap[d]) {
          const onTime = Number(log.count || 0)
          const late = Number(log.count_late || 0)
          dailyMap[d].returns += (onTime + late)
          dailyMap[d].returnsOnTime += onTime
          dailyMap[d].returnsLate += late
          if (onTime > 0) dailyMap[d].details.ops.push({ name: 'Возвращено товаров (вовремя)', value: onTime })
          if (late > 0) dailyMap[d].details.ops.push({ name: 'Возвращено товаров (с опозданием)', value: late })
        }
      })
    }

    // Заполняем зарплатой
    if (rawLogs.salaries) {
      rawLogs.salaries.forEach(sal => {
        if (sal.dailyDetails) {
          Object.entries(sal.dailyDetails).forEach(([date, dayData]) => {
            if (dailyMap[date]) {
              dailyMap[date].salary += Number(dayData?.total || 0)
            }
          })
        }
      })
    }

    // Заполняем финансами (для владельца)
    if (isAdmin) {
      // 1. Доходы WB по дням из calcDailyFin
      const days = Object.keys(dailyMap)
      
      if (useEstimatedRevenue) {
        days.forEach(d => {
          const dayFin = calcDailyFin.dailyData[d]
          if (dayFin && dailyMap[d]) {
            const inc = dayFin.income
            dailyMap[d].income += inc
            
            const label = dayFin.type === 'forecast' 
              ? `ВБ (Прогноз: ${(dayFin.tariffPercent).toFixed(2)}%)` 
              : `ВБ (Факт: ${(dayFin.tariffPercent).toFixed(2)}%)`
            dailyMap[d].details.incomes.push({ name: label, amount: inc })
            
            const tax = inc * 0.06
            dailyMap[d].expense += tax
            dailyMap[d].details.expenses.push({ name: 'Налог УСН 6%', amount: tax })
          }
        })
      } else {
        days.forEach(d => {
          const dayFin = calcDailyFin.dailyData[d]
          if (dayFin && dayFin.type === 'fact' && dailyMap[d]) {
            const inc = dayFin.income
            if (inc > 0) {
              dailyMap[d].income += inc
              dailyMap[d].details.incomes.push({ 
                name: `ВБ (Факт: ${(dayFin.tariffPercent).toFixed(2)}%)`, 
                amount: inc 
              })
              const tax = inc * 0.06
              dailyMap[d].expense += tax
              dailyMap[d].details.expenses.push({ name: 'Налог УСН 6%', amount: tax })
            }
          }
        })
      }

      // 2. Прочие доходы по дням
      const otherIncomeList = (rawLogs.incomes || []).filter(
        i => i.date >= filterStartDate && i.date <= filterEndDate && i.source && i.source !== 'Wildberries'
      )
      otherIncomeList.forEach(i => {
        const inc = Number(i.amount || 0)
        if (dailyMap[i.date]) {
          dailyMap[i.date].income += inc
          dailyMap[i.date].details.incomes.push({ name: i.source || 'Прочий доход', amount: inc })
          
          const tax = inc * 0.06
          dailyMap[i.date].expense += tax
          dailyMap[i.date].details.expenses.push({ name: 'Налог УСН 6%', amount: tax })
        }
      })

      // 3. Переменные расходы
      const selectedPeriodExpenses = (rawLogs.expenses || []).filter(e => e.date >= filterStartDate && e.date <= filterEndDate)
      selectedPeriodExpenses.filter(e => e.type !== 'fixed_payment' && e.is_paid !== false).forEach(e => {
        if (dailyMap[e.date]) {
          dailyMap[e.date].expense += Number(e.amount) || 0
          dailyMap[e.date].details.expenses.push({ name: e.category || 'Расход', amount: Number(e.amount) || 0 })
        }
      })

      // 4. Резервы по дням
      if (rawLogs.reserves) {
        rawLogs.reserves.forEach(r => {
          const d = r.date || r.created_at?.split('T')[0]
          if (dailyMap[d]) {
            const amt = Number(r.amount || 0)
            dailyMap[d].expense += amt
            dailyMap[d].details.expenses.push({ name: 'Ручной резерв', amount: amt })
          }
        })
      }

      // 5. Партнёрские комиссии по дням
      const selectedPeriodPartnerTurnovers = (rawLogs.partnerTurnovers || []).filter(t => {
        const d = t.entry_date?.split('T')[0]
        return d >= filterStartDate && d <= filterEndDate
      })
      selectedPeriodPartnerTurnovers.forEach(t => {
        const d = t.entry_date?.split('T')[0]
        if (dailyMap[d]) {
          dailyMap[d].expense += Number(t.commission_amount || 0)
          dailyMap[d].details.expenses.push({ name: `Партнёрская комиссия`, amount: Number(t.commission_amount || 0) })
        }
      })

      // 6. Начисление прогнозируемых расходов (ФОТ, Переменные, Партнеры) на будущие дни
      if (useEstimatedRevenue && calcDailyFin.lastActualDate) {
        days.forEach(d => {
          if (d > calcDailyFin.lastActualDate && dailyMap[d]) {
            if (averageDailyPayrollPast30 > 0) {
              dailyMap[d].expense += averageDailyPayrollPast30
              dailyMap[d].details.expenses.push({ name: 'ФОТ (Прогноз)', amount: averageDailyPayrollPast30 })
            }
            if (averageDailyVarExpensesPast30 > 0) {
              dailyMap[d].expense += averageDailyVarExpensesPast30
              dailyMap[d].details.expenses.push({ name: 'Перем. расходы (Прогноз)', amount: averageDailyVarExpensesPast30 })
            }
            if (averageDailyPartnerCommissionPast30 > 0) {
              dailyMap[d].expense += averageDailyPartnerCommissionPast30
              dailyMap[d].details.expenses.push({ name: 'Партнёрская комиссия (Прогноз)', amount: averageDailyPartnerCommissionPast30 })
            }
          }
        })
      }

      // 7. ФОТ по дням
      if (rawLogs.salaries) {
        rawLogs.salaries.forEach(sal => {
          if (sal.dailyDetails) {
            Object.entries(sal.dailyDetails).forEach(([date, dayData]) => {
              if (dailyMap[date]) {
                const dayTotal = Number(dayData?.total) || 0
                if (dayTotal > 0) {
                  dailyMap[date].expense += dayTotal
                  dailyMap[date].details.expenses.push({ name: `ФОТ: ${sal.employeeName || 'Сотрудник'}`, amount: dayTotal })
                }
              }
            })
          }
        })
      }

      // Рассчитываем прибыль
      Object.keys(dailyMap).forEach(d => {
        const inc = dailyMap[d].income
        const exp = dailyMap[d].expense
        const actProfit = inc - exp
        dailyMap[d].realProfit = actProfit
        dailyMap[d].profit = Math.max(0, actProfit)
      })
    }

    return Object.values(dailyMap)
  }, [rawLogs, filterStartDate, filterEndDate, isAdmin, useEstimatedRevenue, salaryConfig, averageDailyPayrollPast30, averageDailyVarExpensesPast30, averageDailyPartnerCommissionPast30])

  // Группировка физических операций по их типам (чтобы не отображалась куча одинаковых строчек)
  const groupedOps = useMemo(() => {
    if (!selectedDayDetails?.details?.ops) return []
    const map = {}
    selectedDayDetails.details.ops.forEach(op => {
      map[op.name] = (map[op.name] || 0) + op.value
    })
    return Object.entries(map).map(([name, value]) => ({ name, value }))
  }, [selectedDayDetails])

  // Оптимизированный интерактивный график в useMemo (исключает лаги при открытии модального окна)
  const memoizedChart = useMemo(() => {
    if (chartData.length === 0) return null
    return (
      <ResponsiveContainer width="100%" height={280} minWidth={0}>
        <BarChart 
          data={chartData} 
          onClick={(e) => { 
            if (e && e.activePayload && e.activePayload.length > 0) {
              setSelectedDayDetails(e.activePayload[0].payload)
            }
          }} 
          style={{ cursor: 'pointer' }}
        >
          <XAxis 
            dataKey="displayDate" 
            tick={{ fontSize: 10, fill: 'var(--text-secondary)' }} 
            axisLine={false}
            tickLine={{ stroke: 'var(--surface-border)', strokeWidth: 1 }}
            angle={-45}
            textAnchor="end"
            height={50}
          />
          <YAxis tick={{ fontSize: 10, fill: 'var(--text-secondary)' }} axisLine={false} tickLine={false} />
          
          <RechartsTooltip content={({ active, payload, label }) => {
            if (!active || !payload?.length) return null
            const data = payload[0]?.payload
            if (!data) return null
            
            return activeChartTab === 'ops' ? (
              <div style={{ background: 'var(--surface)', border: '1px solid var(--surface-border)', borderRadius: '12px', padding: '12px 16px', boxShadow: '0 4px 16px rgba(0,0,0,0.12)', fontSize: '13px', minWidth: '180px' }}>
                <div style={{ fontWeight: 700, marginBottom: '8px', fontSize: '14px' }}>{label}</div>
                {chartOpsSettings.issued && (
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '6px' }}>
                    <span style={{ color: 'var(--success)' }}>Выдача</span>
                    <span style={{ fontWeight: 600 }}>{data.issued} шт.</span>
                  </div>
                )}
                {chartOpsSettings.accepted && (
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '6px' }}>
                    <span style={{ color: 'var(--wb-primary)' }}>Приемка</span>
                    <span style={{ fontWeight: 600 }}>{data.accepted} шт.</span>
                  </div>
                )}
                {chartOpsSettings.returns && (
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '6px' }}>
                    <span style={{ color: 'var(--warning)' }}>Возвраты</span>
                    <span style={{ fontWeight: 600 }}>{data.returns} шт.</span>
                  </div>
                )}
                {!isAdmin && chartOpsSettings.salary && (
                  <div style={{ display: 'flex', justifyContent: 'space-between', borderTop: '1px solid var(--surface-border)', paddingTop: '6px', marginTop: '6px' }}>
                    <span style={{ color: '#10B981', fontWeight: 600 }}>ЗП за день</span>
                    <span style={{ fontWeight: 700, color: '#10B981' }}>{formatMoney(data.salary)}</span>
                  </div>
                )}
              </div>
            ) : (
              <div style={{ background: 'var(--surface)', border: '1px solid var(--surface-border)', borderRadius: '12px', padding: '12px 16px', boxShadow: '0 4px 16px rgba(0,0,0,0.12)', fontSize: '13px', minWidth: '200px' }}>
                <div style={{ fontWeight: 700, marginBottom: '8px', fontSize: '14px' }}>{label}</div>
                {chartFinSettings.income && (
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '6px' }}>
                    <span style={{ color: 'var(--success)' }}>Доходы</span>
                    <span style={{ fontWeight: 600, color: 'var(--success)' }}>{formatMoney(data.income)}</span>
                  </div>
                )}
                {chartFinSettings.expense && (
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '6px' }}>
                    <span style={{ color: 'var(--danger)' }}>Расходы</span>
                    <span style={{ fontWeight: 600, color: 'var(--danger)' }}>{formatMoney(data.expense)}</span>
                  </div>
                )}
                {chartFinSettings.profit && (
                  <div style={{ display: 'flex', justifyContent: 'space-between', borderTop: '1px solid var(--surface-border)', paddingTop: '6px' }}>
                    <span style={{ color: 'var(--wb-primary)', fontWeight: 600 }}>Прибыль</span>
                    <span style={{ fontWeight: 700, color: data.realProfit < 0 ? 'var(--danger)' : 'var(--wb-primary)' }}>{formatMoney(data.realProfit)}</span>
                  </div>
                )}
              </div>
            )
          }} />
          
          <Legend verticalAlign="top" height={36} />
          
          {activeChartTab === 'ops' ? (
            <>
              {chartOpsSettings.issued && <Bar dataKey="issued" name="Выдача (шт.)" fill="var(--success)" radius={[4, 4, 0, 0]} isAnimationActive={false} onClick={(data) => { if (data?.payload) setSelectedDayDetails(data.payload) }} />}
              {chartOpsSettings.accepted && <Bar dataKey="accepted" name="Приемка (шт.)" fill="var(--wb-primary)" radius={[4, 4, 0, 0]} isAnimationActive={false} onClick={(data) => { if (data?.payload) setSelectedDayDetails(data.payload) }} />}
              {chartOpsSettings.returns && <Bar dataKey="returns" name="Возвраты (шт.)" fill="var(--warning)" radius={[4, 4, 0, 0]} isAnimationActive={false} onClick={(data) => { if (data?.payload) setSelectedDayDetails(data.payload) }} />}
              {!isAdmin && chartOpsSettings.salary && <Bar dataKey="salary" name="ЗП за день" fill="#10B981" radius={[4, 4, 0, 0]} isAnimationActive={false} onClick={(data) => { if (data?.payload) setSelectedDayDetails(data.payload) }} />}
            </>
          ) : (
            <>
              {chartFinSettings.income && <Bar dataKey="income" name="Доходы" fill="var(--success)" radius={[4, 4, 0, 0]} isAnimationActive={false} onClick={(data) => { if (data?.payload) setSelectedDayDetails(data.payload) }} />}
              {chartFinSettings.expense && <Bar dataKey="expense" name="Расходы" fill="var(--danger)" radius={[4, 4, 0, 0]} isAnimationActive={false} onClick={(data) => { if (data?.payload) setSelectedDayDetails(data.payload) }} />}
              {chartFinSettings.profit && <Bar dataKey="profit" name="Прибыль" fill="var(--wb-primary)" radius={[4, 4, 0, 0]} isAnimationActive={false} onClick={(data) => { if (data?.payload) setSelectedDayDetails(data.payload) }} />}
            </>
          )}
        </BarChart>
      </ResponsiveContainer>
    )
  }, [chartData, activeChartTab, chartOpsSettings, chartFinSettings, isAdmin, setSelectedDayDetails])

  const isToday = filterStartDate === todayIso() && filterEndDate === todayIso()
  const displayDate = isToday 
    ? `Сегодня, ${new Date().toLocaleDateString('ru-RU', { weekday: 'long', day: 'numeric', month: 'long' })}`
    : filterStartDate === filterEndDate
      ? new Date(filterStartDate).toLocaleDateString('ru-RU', { day: 'numeric', month: 'long', year: 'numeric' })
      : `${new Date(filterStartDate).toLocaleDateString('ru-RU', { day: '2-digit', month: '2-digit', year: 'numeric' })} — ${new Date(filterEndDate).toLocaleDateString('ru-RU', { day: '2-digit', month: '2-digit', year: 'numeric' })}`

  const navigateToOperations = (type) => {
    sessionStorage.setItem('ops_drilldown', JSON.stringify({
      startDate: filterStartDate, endDate: filterEndDate, type: type
    }))
    onPageChange('operations')
  }

  const handleSelfWarningClick = (date) => {
    sessionStorage.setItem('ops_drilldown', JSON.stringify({
      startDate: date,
      endDate: date,
      employeeId: profile?.id,
      viewMode: 'chess',
      autoOpenQuickAdd: true
    }))
    onPageChange('operations')
  }

  const handleAdminWarningClick = (date, employeeId) => {
    sessionStorage.setItem('ops_drilldown', JSON.stringify({
      startDate: date,
      endDate: date,
      employeeId: employeeId,
      viewMode: 'chess',
      autoOpenQuickAdd: true
    }))
    onPageChange('operations')
  }

  // Проверка незаполненных операций за последние 30 дней
  useEffect(() => {
    if (!profile?.id || !activePvz?.id || employees.length === 0) return

    const checkMissingOps = async () => {
      try {
        const now = new Date()
        const today = todayIso()
        
        const schedCurrent = await api.getSchedule(now.getFullYear(), now.getMonth() + 1)
        const prevMonth = now.getMonth() === 0 ? 12 : now.getMonth()
        const prevYear = now.getMonth() === 0 ? now.getFullYear() - 1 : now.getFullYear()
        const schedPrev = await api.getSchedule(prevYear, prevMonth)
        const allSched = [...(schedCurrent || []), ...(schedPrev || [])]
        
        const CHECK_DAYS = 30
        const checkDays = []
        for (let i = 1; i <= CHECK_DAYS; i++) {
          const d = new Date(now)
          d.setDate(d.getDate() - i)
          checkDays.push(`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`)
        }
        if (now.getHours() >= 18) {
          checkDays.unshift(today)
        }
        
        const recentShifts = allSched.filter(s => 
          checkDays.includes(s.work_date) && (s.hours > 0 || s.started_at)
        )
        
        if (recentShifts.length === 0) {
          setNoOpsWarning(null)
          return
        }

        const oldest = checkDays[checkDays.length - 1]
        const newest = checkDays[0]
        const allLogs = await api.getWorkLogs('all', { startDate: oldest, endDate: newest, pvzId: activePvz.id })
        
        const logsMap = {}
        ;(allLogs || []).forEach(l => {
          const key = `${l.employee_id}_${l.work_date}`
          if (!logsMap[key]) logsMap[key] = { total: 0, dismissedTypes: [] }
          if (l.comment === '[DISMISSED]' && l.type) {
            logsMap[key].dismissedTypes.push(l.type)
          } else {
            logsMap[key].total += (l.count || 0) + (l.count_late || 0)
          }
        })

        if (!isAdmin) {
          const myShiftsNoOps = []
          for (const shift of recentShifts) {
            if (shift.employee_id !== profile.id) continue
            const key = `${profile.id}_${shift.work_date}`
            const logEntry = logsMap[key]
            
            let isMissing = false
            if (!logEntry) {
              isMissing = true
            } else if (logEntry.total === 0) {
              const isHourlyHelper = profile.role === 'helper' && (salaryConfig?.helperHourlyRate > 0)
              const requiredTypes = isHourlyHelper ? [] : ['acceptance', 'issued', 'returns']
              const missingTypes = requiredTypes.filter(t => !logEntry.dismissedTypes.includes(t))
              if (missingTypes.length > 0) isMissing = true
            }

            if (isMissing) {
              myShiftsNoOps.push(shift.work_date)
            }
          }
          if (myShiftsNoOps.length > 0) {
            const isOnlyToday = myShiftsNoOps.length === 1 && myShiftsNoOps[0] === today
            setNoOpsWarning({ 
              self: true, 
              employees: [], 
              dates: myShiftsNoOps,
              message: isOnlyToday 
                ? 'Вы ещё не внесли операции за сегодня' 
                : `Не заполнены операции за ${myShiftsNoOps.length} смен(ы)`
            })
          } else {
            setNoOpsWarning(null)
          }
        } else {
          const missing = []
          const checked = new Set()
          
          for (const shift of recentShifts) {
            const key = `${shift.employee_id}_${shift.work_date}`
            if (checked.has(key)) continue
            checked.add(key)
            
            const emp = employees.find(e => e.id === shift.employee_id)
            if (!emp || emp.is_legacy || emp.role === 'owner') continue
            
            const logEntry = logsMap[key]
            let isMissing = false
            if (!logEntry) {
              isMissing = true
            } else if (logEntry.total === 0) {
              const isHourlyHelper = emp.role === 'helper' && (salaryConfig?.helperHourlyRate > 0)
              const requiredTypes = isHourlyHelper ? [] : ['acceptance', 'issued', 'returns']
              const missingTypes = requiredTypes.filter(t => !logEntry.dismissedTypes.includes(t))
              if (missingTypes.length > 0) isMissing = true
            }

            if (isMissing) {
              missing.push({ 
                id: shift.employee_id, 
                name: emp.name, 
                date: shift.work_date,
                isToday: shift.work_date === today
              })
            }
          }
          
          missing.sort((a, b) => b.date.localeCompare(a.date))
          setNoOpsWarning(missing.length > 0 ? { self: false, employees: missing } : null)
        }
      } catch (e) {
        console.error('Error checking missing ops:', e)
        setNoOpsWarning(null)
      }
    }

    checkMissingOps()
  }, [profile?.id, activePvz?.id, employees.length, isAdmin])

  return (
    <div className="dashboard-page animate-fade-in">
      <div className="dashboard-header" style={{ flexWrap: 'wrap', gap: '12px', maxWidth: '100%', overflow: 'hidden' }}>
        <div>
          <h1 className="dashboard-title">Дашборд</h1>
          <p className="dashboard-subtitle">{displayDate}</p>
        </div>
        <div data-tour="dashboard-filters" style={{ display: 'flex', flexDirection: 'column', gap: '10px', width: '100%', maxWidth: '500px', overflow: 'hidden' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
          {isAdmin && (
            <div className="accrued-toggle-container">
              <button className={`accrued-toggle-btn ${useEstimatedRevenue ? 'active' : ''}`} onClick={() => setUseEstimatedRevenue(true)}>
                <svg viewBox="0 0 24 24" fill="none" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M3 3v18h18"/><path d="M18 17V9"/><path d="M13 17V5"/><path d="M8 17v-3"/></svg>
                Прогноз
              </button>
              <button className={`accrued-toggle-btn ${!useEstimatedRevenue ? 'active' : ''}`} onClick={() => setUseEstimatedRevenue(false)}>
                <svg viewBox="0 0 24 24" fill="none" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><line x1="12" y1="1" x2="12" y2="23"/><path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"/></svg>
                Касса
              </button>
            </div>
          )}
          {isAdmin && (
              <div style={{ flex: 1, minWidth: '120px' }}>
              <CustomSelect
                value={filterEmployee}
                onChange={setFilterEmployee}
                placeholder="Все сотрудники"
                options={[
                  { value: '', label: 'Все сотрудники' },
                  ...employees.map(emp => ({
                    value: emp.id,
                    label: emp.name,
                  })),
                ]}
              />
            </div>
          )}
          </div>
          <div style={{ width: '100%' }}>
              <DateRangeFilter
                startDate={filterStartDate} endDate={filterEndDate}
                onApply={(s, e) => { setFilterStartDate(s); setFilterEndDate(e) }}
                onReset={() => { setFilterStartDate(monthStartIso()); setFilterEndDate(monthEndIso()) }}
                quickPresets={[
                  { label: 'Сегодня', getValue: () => [todayIso(), todayIso()] },
                  { label: 'Вчера', getValue: () => [yesterdayIso(), yesterdayIso()] },
                  { label: 'Этот месяц', getValue: () => [monthStartIso(), monthEndIso()] },
                  { label: 'Этот квартал', getValue: () => [quarterStartIso(), quarterEndIso()] }
                ]}
                showQuickButtons
              />
            </div>
          </div>
        </div>

      {isAdmin && (
        <OnboardingChecklist
          profile={profile}
          userPvzs={userPvzs}
          employees={employees}
          salaryConfig={salaryConfig}
          onNavigate={(page, params) => onPageChange && onPageChange(page, params)}
        />
      )}

      {loading ? (
        <DashboardSkeleton />
      ) : (
        <>
          {missingTurnoverDays.length > 0 && (
            <div style={{ marginBottom: '24px', padding: '16px', background: 'rgba(239, 68, 68, 0.1)', border: '1px solid rgba(239, 68, 68, 0.3)', borderRadius: '12px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px' }}>
                <span style={{ fontSize: '20px' }}><svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/><line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/></svg></span>
                <h3 style={{ margin: 0, color: 'var(--danger)', fontSize: '16px' }}>Ожидается ввод оборота</h3>
              </div>
              <p style={{ margin: '0 0 16px 0', fontSize: '14px', color: 'var(--text-secondary)' }}>
                Вы настроили расчёт ЗП от процента оборота. Пожалуйста, введите дневной оборот за следующие смены, чтобы зарплата сотрудников рассчиталась корректно:
              </p>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                {missingTurnoverDays.map(date => (
                  <div key={date} style={{ display: 'flex', alignItems: 'center', gap: '12px', background: 'var(--surface)', padding: '12px', borderRadius: '8px' }}>
                    <div style={{ fontWeight: 500, minWidth: '100px' }}>
                      {new Date(date).toLocaleDateString('ru-RU', { day: 'numeric', month: 'long' })}
                    </div>
                    <div style={{ flex: 1, position: 'relative' }}>
                      <input
                        type="number"
                        className="input"
                        placeholder="Оборот из программы WB"
                        value={turnoverInputs[date] || ''}
                        onChange={e => setTurnoverInputs({ ...turnoverInputs, [date]: e.target.value })}
                        style={{ width: '100%', marginBottom: 0 }}
                      />
                      <span style={{ position: 'absolute', right: '12px', top: '50%', transform: 'translateY(-50%)', color: 'var(--text-secondary)' }}>₽</span>
                    </div>
                    <button 
                      className="btn btn-primary"
                      onClick={() => handleSaveTurnover(date)}
                      disabled={!turnoverInputs[date] || Number(turnoverInputs[date]) <= 0}
                    >
                      Сохранить
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Уведомление о незаполненных операциях */}
          {noOpsWarning && (
            <div style={{ 
              marginBottom: '20px', 
              padding: '16px 20px', 
              background: 'linear-gradient(135deg, rgba(251, 191, 36, 0.12), rgba(245, 158, 11, 0.08))', 
              border: '1px solid rgba(245, 158, 11, 0.35)', 
              borderRadius: '14px',
              display: 'flex',
              alignItems: 'flex-start',
              gap: '14px'
            }}>
              <span style={{ fontSize: '28px', lineHeight: 1 }}><svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M16 4h2a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h2"/><rect x="8" y="2" width="8" height="4" rx="1"/></svg></span>
              <div style={{ flex: 1 }}>
                {noOpsWarning.self ? (
                  <>
                    <div style={{ fontWeight: 600, fontSize: '15px', color: 'var(--text-primary)', marginBottom: '4px' }}>
                      {noOpsWarning.message || 'Не заполнены операции'}
                    </div>
                    <div style={{ fontSize: '13px', color: 'var(--text-secondary)', marginBottom: '10px' }}>
                      Заполните приёмку, выдачу и возвраты — от этого зависит ваша зарплата.
                    </div>
                    {noOpsWarning.dates && noOpsWarning.dates.length > 0 && (
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', marginBottom: '12px' }}>
                        {noOpsWarning.dates.map(date => (
                          <span 
                            key={date} 
                            onClick={() => handleSelfWarningClick(date)}
                            title="Нажмите для быстрого заполнения этой смены"
                            style={{ 
                              fontSize: '12px', padding: '4px 12px', borderRadius: '16px', 
                              background: 'rgba(245, 158, 11, 0.15)', color: 'var(--text-primary)', fontWeight: 500,
                              cursor: 'pointer', transition: 'all 0.2s ease', display: 'inline-block'
                            }}
                            onMouseEnter={e => e.target.style.background = 'rgba(245, 158, 11, 0.28)'}
                            onMouseLeave={e => e.target.style.background = 'rgba(245, 158, 11, 0.15)'}
                          >
                            {date === todayIso() ? 'Сегодня' : new Date(date + 'T00:00:00').toLocaleDateString('ru-RU', { day: 'numeric', month: 'short' })}
                          </span>
                        ))}
                      </div>
                    )}
                    <button 
                      className="btn btn-primary" 
                      style={{ fontSize: '13px', padding: '8px 20px', borderRadius: '10px' }}
                      onClick={() => handleSelfWarningClick(noOpsWarning.dates?.[0] || todayIso())}
                    >
                      Быстро заполнить
                    </button>
                  </>
                ) : (
                  <>
                    <div style={{ fontWeight: 600, fontSize: '15px', color: 'var(--text-primary)', marginBottom: '8px' }}>
                      Незаполненные операции за смены (нажмите для перехода)
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                      {noOpsWarning.employees.map((emp, i) => (
                        <div 
                          key={`${emp.id}_${emp.date}_${i}`} 
                          onClick={() => handleAdminWarningClick(emp.date, emp.id)}
                          title="Нажмите для быстрого перехода к заполнению смены"
                          style={{ 
                            display: 'flex', alignItems: 'center', gap: '8px',
                            fontSize: '13px', padding: '6px 14px', borderRadius: '10px', 
                            background: 'rgba(245, 158, 11, 0.1)', cursor: 'pointer',
                            transition: 'all 0.2s ease', width: 'fit-content'
                          }}
                          onMouseEnter={e => e.currentTarget.style.background = 'rgba(245, 158, 11, 0.22)'}
                          onMouseLeave={e => e.currentTarget.style.background = 'rgba(245, 158, 11, 0.1)'}
                        >
                          <span style={{ fontWeight: 600, color: 'var(--text-primary)' }}>{emp.name}</span>
                          <span style={{ color: 'var(--text-tertiary)' }}>•</span>
                          <span style={{ color: 'var(--text-secondary)', fontSize: '12px' }}>
                            {emp.isToday ? 'Сегодня' : new Date(emp.date + 'T00:00:00').toLocaleDateString('ru-RU', { day: 'numeric', month: 'short' })}
                          </span>
                        </div>
                      ))}
                    </div>
                  </>
                )}
              </div>
            </div>
          )}

          {/* Баннер — рекомендация настроить PIN */}
          {profile && !profile.pin_hash && !localStorage.getItem('pin_banner_dismissed') && (
            <div style={{ 
              marginBottom: '20px', 
              padding: '16px 20px', 
              background: 'linear-gradient(135deg, rgba(124, 58, 237, 0.08), rgba(203, 17, 171, 0.06))', 
              border: '1px solid rgba(124, 58, 237, 0.25)', 
              borderRadius: '14px',
              display: 'flex',
              alignItems: 'flex-start',
              gap: '14px'
            }}>
              <span style={{ fontSize: '28px', lineHeight: 1 }}><svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="11" width="18" height="11" rx="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/><circle cx="12" cy="16" r="1"/></svg></span>
              <div style={{ flex: 1 }}>
                <div style={{ fontWeight: 600, fontSize: '15px', color: 'var(--text-primary)', marginBottom: '4px' }}>
                  Новинка: PIN-код для защиты аккаунта
                </div>
                <div style={{ fontSize: '13px', color: 'var(--text-secondary)', marginBottom: '12px', lineHeight: '1.5' }}>
                  Если вы работаете на общем компьютере, настройте 4-значный PIN-код. Он защитит ваш аккаунт от доступа коллег — никто не увидит вашу зарплату, чаты и личные данные.
                </div>
                <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                  <button 
                    className="btn btn-primary" 
                    style={{ fontSize: '13px', padding: '8px 20px', borderRadius: '10px' }}
                    onClick={() => onPageChange('settings', { tab: 'account' })}
                  >
                    Настроить PIN
                  </button>
                  <button 
                    className="btn btn-ghost" 
                    style={{ fontSize: '13px', padding: '8px 16px', color: 'var(--text-tertiary)' }}
                    onClick={(e) => {
                      localStorage.setItem('pin_banner_dismissed', '1')
                      e.target.closest('[style]').parentElement.style.display = 'none'
                    }}
                  >
                    Позже
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* KPI физических и финансовых показателей */}
          <div data-tour="dashboard-stats" style={{ display: 'flex', flexDirection: 'column', gap: '20px', width: '100%' }}>
            <FinanceStatsCards stats={stats} profile={profile} onPageChange={onPageChange} />
            <OpsStatsCards stats={stats} navigateToOperations={navigateToOperations} onPageChange={onPageChange} />
          </div>

          <div className="dashboard-main-layout" data-tour="dashboard-chart" style={{ display: 'flex', gap: '20px', marginTop: '20px', flexWrap: 'wrap' }}>
            {/* Левая колонка - График */}
            <div style={{ flex: '2', minWidth: '350px' }}>
              {chartData.length > 0 && (
                <div style={{ background: 'var(--surface)', border: '1px solid var(--surface-border)', borderRadius: '16px', padding: '20px', boxShadow: '0 4px 20px rgba(0,0,0,0.04)' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '12px', marginBottom: '16px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <span style={{ fontSize: '20px' }}><svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="23 6 13.5 15.5 8.5 10.5 1 18"/><polyline points="17 6 23 6 23 12"/></svg></span>
                      <h3 style={{ margin: 0, fontSize: '16px', fontWeight: 700, color: 'var(--text-primary)' }}>Аналитика по дням</h3>
                      <span style={{ fontSize: '11px', color: 'var(--text-tertiary)', fontWeight: 400, marginLeft: '6px' }}>клик для деталей</span>
                    </div>
                    
                    {/* Сегментный переключатель вкладок — отображается ТОЛЬКО Владельцу */}
                    {profile?.role === 'owner' && (
                      <div style={{ display: 'flex', background: 'var(--bg-secondary)', padding: '3px', borderRadius: '8px', border: '1px solid var(--surface-border)' }}>
                        <button 
                          onClick={() => setActiveChartTab('ops')} 
                          style={{ 
                            fontSize: '11px', 
                            padding: '6px 12px', 
                            borderRadius: '6px', 
                            border: 'none', 
                            fontWeight: 600,
                            cursor: 'pointer',
                            background: activeChartTab === 'ops' ? 'var(--wb-primary)' : 'transparent',
                            color: activeChartTab === 'ops' ? '#fff' : 'var(--text-secondary)',
                            transition: 'all 0.2s ease'
                          }}
                        >
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="16.5" y1="9.4" x2="7.5" y2="4.21"/><path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z"/><polyline points="3.27 6.96 12 12.01 20.73 6.96"/><line x1="12" y1="22.08" x2="12" y2="12"/></svg> Операции
                        </button>
                        <button 
                          onClick={() => setActiveChartTab('finance')} 
                          style={{ 
                            fontSize: '11px', 
                            padding: '6px 12px', 
                            borderRadius: '6px', 
                            border: 'none', 
                            fontWeight: 600,
                            cursor: 'pointer',
                            background: activeChartTab === 'finance' ? 'var(--wb-primary)' : 'transparent',
                            color: activeChartTab === 'finance' ? '#fff' : 'var(--text-secondary)',
                            transition: 'all 0.2s ease'
                          }}
                        >
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="12" y1="1" x2="12" y2="23"/><path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"/></svg> Финансы
                        </button>
                      </div>
                    )}
                  </div>

                  {/* Чекбоксы настроек отображения */}
                  <div style={{ display: 'flex', gap: '12px', alignItems: 'center', flexWrap: 'wrap', fontSize: '12px', background: 'var(--bg-secondary)', padding: '8px 14px', borderRadius: '8px', border: '1px solid var(--surface-border)', marginBottom: '16px' }}>
                    <span style={{ color: 'var(--text-secondary)', fontWeight: 600, marginRight: '4px' }}>Отображать:</span>
                    {activeChartTab === 'ops' ? (
                      <>
                        <label style={{ display: 'flex', alignItems: 'center', gap: '6px', cursor: 'pointer', userSelect: 'none', fontWeight: 500 }}>
                          <input type="checkbox" checked={chartOpsSettings.issued} onChange={(e) => setChartOpsSettings({ ...chartOpsSettings, issued: e.target.checked })} style={{ accentColor: 'var(--success)' }} />
                          Выдача
                        </label>
                        <label style={{ display: 'flex', alignItems: 'center', gap: '6px', cursor: 'pointer', userSelect: 'none', fontWeight: 500 }}>
                          <input type="checkbox" checked={chartOpsSettings.accepted} onChange={(e) => setChartOpsSettings({ ...chartOpsSettings, accepted: e.target.checked })} style={{ accentColor: 'var(--wb-primary)' }} />
                          Приемка
                        </label>
                        <label style={{ display: 'flex', alignItems: 'center', gap: '6px', cursor: 'pointer', userSelect: 'none', fontWeight: 500 }}>
                          <input type="checkbox" checked={chartOpsSettings.returns} onChange={(e) => setChartOpsSettings({ ...chartOpsSettings, returns: e.target.checked })} style={{ accentColor: 'var(--warning)' }} />
                          Возвраты
                        </label>
                        {!isAdmin && (
                          <label style={{ display: 'flex', alignItems: 'center', gap: '6px', cursor: 'pointer', userSelect: 'none', fontWeight: 500 }}>
                            <input type="checkbox" checked={chartOpsSettings.salary} onChange={(e) => setChartOpsSettings({ ...chartOpsSettings, salary: e.target.checked })} style={{ accentColor: '#10B981' }} />
                            Заработанная ЗП
                          </label>
                        )}
                      </>
                    ) : (
                      <>
                        <label style={{ display: 'flex', alignItems: 'center', gap: '6px', cursor: 'pointer', userSelect: 'none', fontWeight: 500 }}>
                          <input type="checkbox" checked={chartFinSettings.income} onChange={(e) => setChartFinSettings({ ...chartFinSettings, income: e.target.checked })} style={{ accentColor: 'var(--success)' }} />
                          Доходы
                        </label>
                        <label style={{ display: 'flex', alignItems: 'center', gap: '6px', cursor: 'pointer', userSelect: 'none', fontWeight: 500 }}>
                          <input type="checkbox" checked={chartFinSettings.expense} onChange={(e) => setChartFinSettings({ ...chartFinSettings, expense: e.target.checked })} style={{ accentColor: 'var(--danger)' }} />
                          Расходы
                        </label>
                        <label style={{ display: 'flex', alignItems: 'center', gap: '6px', cursor: 'pointer', userSelect: 'none', fontWeight: 500 }}>
                          <input type="checkbox" checked={chartFinSettings.profit} onChange={(e) => setChartFinSettings({ ...chartFinSettings, profit: e.target.checked })} style={{ accentColor: 'var(--wb-primary)' }} />
                          Прибыль
                        </label>
                      </>
                    )}
                  </div>

                  {/* Оптимизированный интерактивный график */}
                  <div style={{ height: '280px', width: '100%' }}>
                    {memoizedChart}
                  </div>

                  {/* Средние значения по типам операций */}
                  {activeChartTab === 'ops' && chartData.length > 0 && (() => {
                    const daysWithOps = chartData.filter(d => (d.issued + d.accepted + d.returns) > 0).length;
                    if (daysWithOps === 0) return null;
                    const avgIssued = Math.round(stats.issued / daysWithOps);
                    const avgAccepted = Math.round(stats.accepted / daysWithOps);
                    const totalReturnsItems = (stats.returnsOnTime || 0) + (stats.returnsLate || 0);
                    const avgReturns = Math.round(totalReturnsItems / daysWithOps);
                    return (
                      <div style={{ display: 'flex', gap: '20px', flexWrap: 'wrap', padding: '12px 0 0', borderTop: '1px solid var(--surface-border)', marginTop: '12px', alignItems: 'center' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '13px' }}>
                          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="var(--text-tertiary)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="22 12 18 12 15 21 9 3 6 12 2 12"/></svg>
                          <span style={{ color: 'var(--text-secondary)' }}>Среднее в день:</span>
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '13px' }}>
                          <span style={{ width: 8, height: 8, borderRadius: '50%', background: 'var(--success)', display: 'inline-block', flexShrink: 0 }}></span>
                          <span style={{ color: 'var(--text-secondary)' }}>Выдача</span>
                          <span style={{ fontWeight: 700, color: 'var(--text-primary)' }}>{avgIssued}</span>
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '13px' }}>
                          <span style={{ width: 8, height: 8, borderRadius: '50%', background: 'var(--wb-primary)', display: 'inline-block', flexShrink: 0 }}></span>
                          <span style={{ color: 'var(--text-secondary)' }}>Приёмка</span>
                          <span style={{ fontWeight: 700, color: 'var(--text-primary)' }}>{avgAccepted}</span>
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '13px' }}>
                          <span style={{ width: 8, height: 8, borderRadius: '50%', background: 'var(--warning)', display: 'inline-block', flexShrink: 0 }}></span>
                          <span style={{ color: 'var(--text-secondary)' }}>Возвраты</span>
                          <span style={{ fontWeight: 700, color: 'var(--text-primary)' }}>{avgReturns} шт.</span>
                        </div>
                        <span style={{ fontSize: '11px', color: 'var(--text-tertiary)', marginLeft: 'auto' }}>за {daysWithOps} {daysWithOps === 1 ? 'день' : daysWithOps < 5 ? 'дня' : 'дней'}</span>
                      </div>
                    );
                  })()}

                  {/* Средние финансовые показатели */}
                  {activeChartTab === 'finance' && chartData.length > 0 && (() => {
                    const daysWithFinance = chartData.filter(d => d.income > 0 || d.expense > 0).length;
                    if (daysWithFinance === 0) return null;
                    const totalIncome = chartData.reduce((s, d) => s + (d.income || 0), 0);
                    const totalExpense = chartData.reduce((s, d) => s + (d.expense || 0), 0);
                    const totalProfit = chartData.reduce((s, d) => s + (d.profit || 0), 0);
                    const avgIncome = Math.round(totalIncome / daysWithFinance);
                    const avgExpense = Math.round(totalExpense / daysWithFinance);
                    const avgProfit = Math.round(totalProfit / daysWithFinance);
                    return (
                      <div style={{ display: 'flex', gap: '20px', flexWrap: 'wrap', padding: '12px 0 0', borderTop: '1px solid var(--surface-border)', marginTop: '12px', alignItems: 'center' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '13px' }}>
                          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="var(--text-tertiary)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="22 12 18 12 15 21 9 3 6 12 2 12"/></svg>
                          <span style={{ color: 'var(--text-secondary)' }}>Среднее в день:</span>
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '13px' }}>
                          <span style={{ width: 8, height: 8, borderRadius: '50%', background: 'var(--success)', display: 'inline-block', flexShrink: 0 }}></span>
                          <span style={{ color: 'var(--text-secondary)' }}>Доход</span>
                          <span style={{ fontWeight: 700, color: 'var(--success)' }}>{formatMoney(avgIncome)}</span>
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '13px' }}>
                          <span style={{ width: 8, height: 8, borderRadius: '50%', background: 'var(--danger)', display: 'inline-block', flexShrink: 0 }}></span>
                          <span style={{ color: 'var(--text-secondary)' }}>Расход</span>
                          <span style={{ fontWeight: 700, color: 'var(--danger)' }}>{formatMoney(avgExpense)}</span>
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '13px' }}>
                          <span style={{ width: 8, height: 8, borderRadius: '50%', background: 'var(--wb-primary)', display: 'inline-block', flexShrink: 0 }}></span>
                          <span style={{ color: 'var(--text-secondary)' }}>Прибыль</span>
                          <span style={{ fontWeight: 700, color: avgProfit >= 0 ? 'var(--success)' : 'var(--danger)' }}>{formatMoney(avgProfit)}</span>
                        </div>
                        <span style={{ fontSize: '11px', color: 'var(--text-tertiary)', marginLeft: 'auto' }}>за {daysWithFinance} {daysWithFinance === 1 ? 'день' : daysWithFinance < 5 ? 'дня' : 'дней'}</span>
                      </div>
                    );
                  })()}
                </div>
              )}

              {isAdmin && (
                <div className="dashboard-panel ai-forecast-panel" style={{ marginTop: '20px', position: 'relative', overflow: 'hidden' }}>
                  {/* Фоновое свечение в стиле Gemini */}
                  <div className="ai-glow-effect"></div>
                  
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px', position: 'relative', zIndex: 2 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <span className="ai-stars-icon"><svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="url(#aiGrad)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><defs><linearGradient id="aiGrad" x1="0%" y1="0%" x2="100%" y2="100%"><stop offset="0%" stopColor="#6366F1"/><stop offset="100%" stopColor="#A855F7"/></linearGradient></defs><polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/></svg></span>
                      <h3 style={{ margin: 0, fontSize: '16px', fontWeight: 700, color: 'var(--text-primary)' }}>
                        AI Прогноз нагрузки и смен
                      </h3>
                      <span className="badge badge-accent" style={{ fontSize: '9px', padding: '2px 6px', background: 'linear-gradient(135deg, #6366F1, #A855F7)', color: '#fff', border: 'none' }}>Gemini Pro</span>
                    </div>
                    <button 
                      className={`btn btn-ghost btn-sm ${loadingForecast ? 'loading' : ''}`} 
                      onClick={() => loadForecast(true)} 
                      disabled={loadingForecast}
                      style={{ fontSize: '12px', color: 'var(--wb-primary)', display: 'flex', alignItems: 'center', gap: '4px' }}
                    >
                      <svg className={loadingForecast ? 'spin-animation' : ''} width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{ transform: loadingForecast ? 'rotate(360deg)' : 'none', transition: loadingForecast ? 'transform 1s linear infinite' : 'none' }}>
                        <path d="M21.5 2v6h-6M21.34 15.57a10 10 0 1 1-.57-8.38l5.67-5.67"/>
                      </svg>
                      {loadingForecast ? 'Анализ...' : 'Обновить'}
                    </button>
                  </div>

                  {loadingForecast ? (
                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', padding: '30px 10px', gap: '12px', position: 'relative', zIndex: 2 }}>
                      <div className="ai-loading-spinner">
                        <div className="double-bounce1"></div>
                        <div className="double-bounce2"></div>
                      </div>
                      <span style={{ fontSize: '13px', color: 'var(--text-secondary)', textAlign: 'center', fontWeight: 500 }}>
                        ИИ анализирует 45 дней истории операций, объемы приёмки/выдачи и плановый график смен...
                      </span>
                    </div>
                  ) : forecastError ? (
                    <div className="error-state" style={{ padding: '20px', textAlign: 'center', position: 'relative', zIndex: 2 }}>
                      <p style={{ color: 'var(--danger)', margin: '0 0 12px 0', fontSize: '13px' }}>{forecastError}</p>
                      <button className="btn btn-primary btn-sm" onClick={() => loadForecast(false)}>Повторить попытку</button>
                    </div>
                  ) : forecast ? (
                    <div style={{ position: 'relative', zIndex: 2 }}>
                      {forecast.staleWarning && (
                        <div style={{ marginBottom: '12px', padding: '8px 12px', borderRadius: '6px', background: 'rgba(245, 158, 11, 0.1)', border: '1px solid rgba(245, 158, 11, 0.3)', color: '#d97706', fontSize: '12px', display: 'flex', alignItems: 'center', gap: '6px' }}>
                          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>
                          <span>{forecast.staleWarning}</span>
                        </div>
                      )}
                      {forecast.summary && (
                        <div className="ai-summary-box" style={{ marginBottom: '16px' }}>
                          <span style={{ fontSize: '16px', marginRight: '6px', display: 'inline-flex', verticalAlign: 'middle' }}><svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#F59E0B" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M9 18h6"/><path d="M10 22h4"/><path d="M12 2a7 7 0 0 0-4 12.7V17h8v-2.3A7 7 0 0 0 12 2z"/></svg></span>
                          <span style={{ fontSize: '13px', color: 'var(--text-primary)', lineHeight: '1.5', fontWeight: 500 }}>
                            {forecast.summary}
                          </span>
                        </div>
                      )}

                      <div className="ai-forecast-scroll-container">
                        {forecast.forecast?.map((day, idx) => {
                          const isHigh = day.workload === 'High';
                          const isLow = day.workload === 'Low';
                          
                          // Цветовое оформление для карточек дней
                          let badgeBg = 'var(--bg-secondary)';
                          let badgeColor = 'var(--text-secondary)';
                          let cardBorder = 'var(--surface-border)';
                          
                          if (isHigh) {
                            badgeBg = 'rgba(239, 68, 68, 0.15)';
                            badgeColor = 'var(--danger)';
                            cardBorder = 'rgba(239, 68, 68, 0.25)';
                          } else if (isLow) {
                            badgeBg = 'rgba(16, 185, 129, 0.15)';
                            badgeColor = '#10B981';
                            cardBorder = 'rgba(16, 185, 129, 0.25)';
                          } else {
                            badgeBg = 'rgba(245, 158, 11, 0.15)';
                            badgeColor = '#F59E0B';
                            cardBorder = 'rgba(245, 158, 11, 0.25)';
                          }

                          // Проверка наличия предупреждений в рекомендации
                          const hasWarning = day.recommendation && day.recommendation !== 'Смена укомплектована оптимально.' && !day.recommendation.includes('оптимально');

                          return (
                            <div 
                              key={idx} 
                              className={`ai-day-card ${hasWarning ? 'warning-day' : ''}`}
                              style={{ border: `1px solid ${cardBorder}` }}
                            >
                              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                                <div style={{ display: 'flex', flexDirection: 'column' }}>
                                  <span style={{ fontWeight: 700, fontSize: '13px', color: 'var(--text-primary)' }}>{day.dayOfWeek}</span>
                                  <span style={{ fontSize: '10px', color: 'var(--text-tertiary)' }}>{day.date.split('-').reverse().slice(0, 2).join('.')}</span>
                                </div>
                                <span 
                                  style={{ 
                                    fontSize: '9px', 
                                    padding: '2px 6px', 
                                    borderRadius: '12px', 
                                    fontWeight: 700,
                                    background: badgeBg, 
                                    color: badgeColor,
                                    textTransform: 'uppercase'
                                  }}
                                >
                                  {day.workload === 'High' ? 'Пик' : day.workload === 'Low' ? 'Спад' : 'Норма'}
                                </span>
                              </div>

                              <div style={{ display: 'flex', gap: '8px', marginBottom: '8px', padding: '6px 8px', background: 'var(--bg-secondary)', borderRadius: '8px' }}>
                                <div style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                                  <span style={{ fontSize: '9px', color: 'var(--text-tertiary)' }}>Приёмка</span>
                                  <span style={{ fontSize: '13px', fontWeight: 700, color: 'var(--text-primary)' }}>{day.expectedAcceptance}</span>
                                </div>
                                <div style={{ width: '1px', background: 'var(--surface-border)' }}></div>
                                <div style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                                  <span style={{ fontSize: '9px', color: 'var(--text-tertiary)' }}>Выдача</span>
                                  <span style={{ fontSize: '13px', fontWeight: 700, color: 'var(--text-primary)' }}>{day.expectedIssued}</span>
                                </div>
                              </div>

                              <div style={{ fontSize: '10px', color: 'var(--text-secondary)', marginBottom: '8px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{verticalAlign: 'middle', marginRight: '3px'}}><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/></svg>{day.scheduledStaff && day.scheduledStaff.length > 0 
                                  ? day.scheduledStaff.join(', ') 
                                  : <span style={{ color: 'var(--danger)', fontWeight: 600 }}>Нет сотрудников!</span>
                                }
                              </div>

                              {day.recommendation && (
                                <div 
                                  style={{ 
                                    fontSize: '10px', 
                                    lineHeight: '1.3',
                                    padding: '6px 8px',
                                    borderRadius: '6px',
                                    background: hasWarning ? 'rgba(239, 68, 68, 0.05)' : 'var(--bg-secondary)',
                                    color: hasWarning ? 'var(--danger)' : 'var(--text-secondary)',
                                    borderLeft: `2.5px solid ${hasWarning ? 'var(--danger)' : 'var(--text-tertiary)'}`,
                                    fontWeight: hasWarning ? 600 : 400
                                  }}
                                >
                                  {day.recommendation}
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  ) : (
                    <div style={{ textAlign: 'center', padding: '20px', position: 'relative', zIndex: 2 }}>
                      <button className="btn btn-primary" onClick={() => loadForecast(false)}>Сгенерировать AI-прогноз</button>
                    </div>
                  )}
                </div>
              )}
            </div>


            {/* Правая колонка - Выплаты и действия */}
            <div style={{ flex: '1', minWidth: '300px', display: 'flex', flexDirection: 'column', gap: '20px' }}>
              <div className="dashboard-panel" style={{ display: 'flex', flexDirection: 'column' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
                  <h3 className="panel-title" style={{ margin: 0 }}><svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="4" width="18" height="18" rx="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></svg> График выплат ЗП</h3>
                  <button className="btn btn-ghost btn-sm" onClick={() => onPageChange('payments')} style={{ fontSize: '12px', padding: '4px 8px', color: 'var(--wb-primary)' }}>Детали ЗП →</button>
                </div>
                {loadingPayouts || salaryCandidatesStatus === 'loading' ? (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                    {[1, 2, 3].map(i => (
                      <div key={i} className="skeleton" style={{ height: '60px', borderRadius: '12px' }}></div>
                    ))}
                  </div>
                ) : salaryCandidatesStatus === 'error' ? (
                  <div className="empty-state" style={{ padding: '24px', textAlign: 'center' }}>
                    <span className="empty-state-icon" style={{ color: 'var(--danger, #EF4444)', display: 'inline-flex', marginBottom: '8px' }}>
                      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/>
                      </svg>
                    </span>
                    <p className="empty-state-text" style={{ color: 'var(--text-primary)', fontWeight: 600, margin: '0 0 4px 0' }}>Не удалось загрузить данные о выплатах</p>
                    <p style={{ fontSize: '12px', color: 'var(--text-secondary)', margin: '0 0 12px 0' }}>{salaryCandidatesError || 'Данные недоступны'}</p>
                    <button
                      type="button"
                      className="btn btn-secondary btn-sm"
                      onClick={() => setPayoutsRefreshKey(k => k + 1)}
                      style={{ fontSize: '12px', padding: '6px 14px' }}
                    >
                      Повторить
                    </button>
                  </div>
                ) : upcomingPayouts.length === 0 ? (
                  <div className="empty-state" style={{ padding: '24px' }}>
                    <span className="empty-state-icon"><svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="4" width="18" height="18" rx="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></svg></span>
                    <p className="empty-state-text">Список сотрудников пуст или ЗП не настроена.</p>
                  </div>
                ) : (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', overflowY: 'auto', maxHeight: '250px', paddingRight: '4px' }}>
                    {upcomingPayouts.map(payout => (
                      <div key={payout.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px 14px', background: 'var(--surface-hover)', borderRadius: '12px', border: '1px solid var(--surface-border)' }}>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                            <span style={{ fontWeight: 600, fontSize: '13px', color: 'var(--text-primary)' }}>{payout.name}</span>
                            <span className={`badge ${payout.role === 'helper' ? 'badge-warning' : 'badge-primary'}`} style={{ fontSize: '9px', padding: '1px 4px' }}>
                              {payout.role === 'helper' ? 'Помощник' : 'Сотрудник'}
                            </span>
                            {(payout.is_legacy || payout.isLegacy) && (
                              <span className="badge badge-secondary" style={{ fontSize: '9px', padding: '1px 4px', background: 'rgba(156, 163, 175, 0.2)', color: 'var(--text-muted)' }}>
                                Уволен
                              </span>
                            )}
                          </div>
                          <div style={{ fontSize: '11px', color: 'var(--text-secondary)' }}>Срок: <span>{payout.payoutDate}</span></div>
                        </div>
                        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '4px' }}>
                          {payout.financialDataUnavailable || payout.status === 'data_unavailable' ? (
                            <span
                              title={payout.notice || 'Данные недоступны'}
                              style={{ fontSize: '11px', fontWeight: 600, color: 'var(--warning, #F59E0B)', display: 'flex', alignItems: 'center', gap: '4px' }}
                            >
                              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/>
                              </svg>
                              Данные недоступны
                            </span>
                          ) : (
                            <span style={{ fontSize: '13px', fontWeight: 700, color: payout.amount > 0 ? '#10B981' : 'var(--text-secondary)' }}>
                              {formatMoney(payout.amount)}
                            </span>
                          )}
                          {isAdmin && payout.amount > 0 && !payout.financialDataUnavailable && payout.status !== 'data_unavailable' && (
                            <button
                              type="button"
                              className="btn btn-primary"
                              style={{ fontSize: '10px', padding: '3px 6px', marginTop: '2px', borderRadius: '6px' }}
                              onClick={(e) => {
                                e.stopPropagation()
                                setPaymentError(null)
                                setPaymentModalPayout(payout)
                              }}
                            >
                              Оплатить
                            </button>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <div className="dashboard-panel">
                <h3 className="panel-title">Быстрые действия</h3>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                   <button onClick={() => onPageChange('operations')} className="btn btn-secondary" style={{ width: '100%', justifyContent: 'flex-start' }}><svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M16 4h2a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h2"/><rect x="8" y="2" width="8" height="4" rx="1"/></svg> Добавить операции</button>
                   <button onClick={() => onPageChange('operations')} className="btn btn-secondary" style={{ width: '100%', justifyContent: 'flex-start' }}><svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M14.7 6.3a1 1 0 0 0 0 1.4l1.6 1.6a1 1 0 0 0 1.4 0l3.77-3.77a6 6 0 0 1-7.94 7.94l-6.91 6.91a2.12 2.12 0 0 1-3-3l6.91-6.91a6 6 0 0 1 7.94-7.94l-3.76 3.76z"/></svg> Дополнительные работы</button>
                   <button onClick={() => onPageChange('violations')} className="btn btn-secondary" style={{ width: '100%', justifyContent: 'flex-start', color: 'var(--danger)', borderColor: 'rgba(239, 68, 68, 0.2)' }}><svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/><line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/></svg> Сообщить об ошибке</button>
                </div>
              </div>
            </div>
          </div>

          {/* Модальное окно подтверждения выплаты */}
          {paymentModalPayout && (
            <Modal
              isOpen={!!paymentModalPayout}
              onClose={() => !paymentSubmitting && setPaymentModalPayout(null)}
              title="Подтверждение выплаты"
            >
              <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                <p style={{ margin: 0, fontSize: '14px', color: 'var(--text-primary)' }}>
                  Вы действительно хотите провести выплату <strong>{formatMoney(paymentModalPayout.amount)}</strong> для сотрудника <strong>{paymentModalPayout.name}</strong> за период <strong>{paymentModalPayout.periodLabel}</strong>?
                </p>
                {paymentError && (
                  <div style={{ color: 'var(--danger)', fontSize: '13px' }}>
                    {paymentError}
                  </div>
                )}
                <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px', marginTop: '8px' }}>
                  <button
                    type="button"
                    className="btn btn-secondary"
                    disabled={paymentSubmitting}
                    onClick={() => setPaymentModalPayout(null)}
                  >
                    Отмена
                  </button>
                  <button
                    type="button"
                    className="btn btn-primary"
                    disabled={paymentSubmitting}
                    onClick={async () => {
                      setPaymentSubmitting(true)
                      setPaymentError(null)
                      try {
                        await api.createPayment({
                          employee_id: paymentModalPayout.id,
                          type: 'salary',
                          amount: paymentModalPayout.amount,
                          period_start: paymentModalPayout.startDate,
                          period_end: paymentModalPayout.endDate,
                          created_by: profile?.id
                        })
                        setPaymentModalPayout(null)
                        setPayoutsRefreshKey(k => k + 1)
                      } catch (err) {
                        console.error('Failed to create payment:', err)
                        setPaymentError(err.message || 'Ошибка при проведении выплаты')
                      } finally {
                        setPaymentSubmitting(false)
                      }
                    }}
                  >
                    {paymentSubmitting ? 'Проведение...' : 'Подтвердить оплату'}
                  </button>
                </div>
              </div>
            </Modal>
          )}



          {/* Детализация кликнутого дня */}
          <DayDetailsModal 
            selectedDayDetails={selectedDayDetails} 
            setSelectedDayDetails={setSelectedDayDetails} 
            groupedOps={groupedOps} 
            isAdmin={isAdmin} 
            profile={profile} 
          />
        </>
      )}
    </div>
  )
}
